import { Link, useSearchParams } from "react-router-dom";

import { LogoImage } from "../components/BrandLogo";
import { PublicShell } from "../components/PublicShell";
import { useAuth } from "../hooks/useAuth";
import { useT } from "../i18n/LanguageProvider";
import { useIsMobile } from "../hooks/useIsMobile";
import { C } from "../styles/tokens";
import { InlineIcon } from "../components/InlineIcon";

/**
 * Shared "coming soon" page.
 *   /shop         -> variant="shop" ("The Tick & Pass Shop -- Coming soon...")
 *   /coming-soon  -> generic; `?for=Students` shows the community as a pill.
 */
export function ComingSoonPage({ variant }) {
  const t = useT();
  const isMobile = useIsMobile();
  const [params] = useSearchParams();
  const community = params.get("for");

  // Signed-in professionals with a community tag (client requirement 4) get a
  // personalised "you're on the list" state instead of the generic page.
  const { localUser } = useAuth();
  const myCommunity = localUser?.professional?.community ?? null;
  const isEligible = !!myCommunity && (!community || community === myCommunity);

  return (
    <PublicShell>
      <section
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: isMobile ? "56px 20px" : "88px 40px",
          background: `linear-gradient(180deg, ${C.surface} 0%, #fff 100%)`,
        }}
      >
        <LogoImage variant="mark" height={52} />

        {(variant === "shop" ||
          variant === "auth" ||
          variant === "gateway" ||
          variant === "terms" ||
          variant === "privacy") && (
          <div
            style={{
              marginTop: 28,
              fontSize: 14,
              fontWeight: 800,
              color: C.primary,
              textTransform: "uppercase",
              letterSpacing: 1.2,
            }}
          >
            {t(
              variant === "shop"
                ? "coming.shopTitle"
                : variant === "gateway"
                  ? "coming.gatewayTitle"
                  : variant === "terms"
                    ? "coming.termsTitle"
                    : variant === "privacy"
                      ? "coming.privacyTitle"
                      : "coming.authTitle",
            )}
          </div>
        )}

        {community && (
          <div
            style={{
              marginTop: 28,
              display: "inline-block",
              background: C.primaryLight,
              color: C.primaryDark,
              padding: "6px 18px",
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {community}
          </div>
        )}

        <h1
          style={{
            fontSize: isMobile ? 36 : 48,
            fontWeight: 800,
            color: C.navy,
            letterSpacing: -1,
            margin: "14px 0 12px",
            lineHeight: 1.1,
          }}
        >
          {t("coming.title")}
        </h1>

        <p
          style={{
            fontSize: 16,
            color: C.textMed,
            lineHeight: 1.6,
            maxWidth: 440,
            margin: "0 0 30px",
          }}
        >
          {community ? t("coming.offerBody") : t("coming.body")}
        </p>

        {isEligible && variant !== "shop" && (
          <div
            style={{
              margin: "-16px 0 30px",
              padding: "10px 18px",
              background: C.greenLight,
              color: C.green,
              borderRadius: 8,
              fontSize: 13.5,
              fontWeight: 600,
              maxWidth: 440,
              lineHeight: 1.5,
            }}
          >
            <InlineIcon name="check" size={14} /> {t("coming.eligible")} (
            {myCommunity})
          </div>
        )}

        <Link
          to="/"
          style={{
            display: "inline-block",
            padding: "12px 26px",
            fontSize: 15,
            fontWeight: 700,
            color: "#fff",
            background: C.primary,
            borderRadius: 6,
            textDecoration: "none",
          }}
        >
          {t("coming.back")}
        </Link>
      </section>
    </PublicShell>
  );
}
